import { useMemo, useState } from 'react';
import { NJHeading, NJInputSearch, NJLink, NJPagination, NJSelect, NJText } from '@engie-group/fluid-design-system-react';
import type { ProjectSummary } from './types';
import { ProjectAccessAvatars } from './ProjectAccessAvatars';
import './home.css';

type SortKey = 'name' | 'communes' | 'updatedAt' | 'start' | 'end' | 'people';
const HEADERS: { key: SortKey; label: string }[] = [
  { key: 'name', label: 'Nom du projet' }, { key: 'communes', label: 'Périmètre · villes' },
  { key: 'updatedAt', label: 'Dernière modification' }, { key: 'start', label: 'Début du contrat' },
  { key: 'end', label: 'Fin du contrat' }, { key: 'people', label: 'Personnes ayant accès' },
];
const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('fr-FR');
const contractMonth = (value: string) => value.split('-').reverse().join('/');

function modificationTime(value: string): number {
  const [date, time] = value.split(' ');
  const [day, month, year] = date.split('/');
  return new Date(`${year}-${month}-${day}T${time}`).getTime();
}

export function CompletedProjectsTable({ projects, onOpen }: {
  projects: ProjectSummary[];
  onOpen: (id: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ key: SortKey; descending: boolean }>({ key: 'updatedAt', descending: true });
  const rows = useMemo(() => {
    const query = normalize(search.trim());
    return projects.map((project) => ({
      ...project, communes: project.communeNames.join(', '),
      people: [...new Set([project.owner, ...project.members])].join(', '),
      start: project.contract.start, end: project.contract.end,
    })).filter((project) => normalize([
      project.name, project.communes, project.people, project.updatedAt,
      contractMonth(project.start), contractMonth(project.end),
    ].join(' ')).includes(query)).sort((a, b) => {
      const comparison = sort.key === 'updatedAt'
        ? modificationTime(a.updatedAt) - modificationTime(b.updatedAt)
        : a[sort.key].localeCompare(b[sort.key], 'fr-FR', { numeric: true });
      return sort.descending ? -comparison : comparison;
    });
  }, [projects, search, sort]);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const start = (currentPage - 1) * pageSize;
  const visibleRows = rows.slice(start, start + pageSize);

  return (
    <section className="pcv3-completed" aria-labelledby="pcv3-completed-title">
      <div className="pcv3-section-heading">
        <NJHeading as="h2" scale="md" id="pcv3-completed-title" aria-live="polite">Projets terminés ({rows.length})</NJHeading>
        <div className="pcv3-project-search" onClick={(event) => {
          // This Fluid version clears the input without notifying onChange.
          if (event.target instanceof Element && event.target.closest('button')) {
            setSearch('');
            setPage(1);
          }
        }}>
          <NJInputSearch id="pcv3-completed-search" label="Rechercher dans les projets terminés"
            aria-label="Rechercher dans les projets terminés" placeholder="Nom, commune, personne…"
            value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} resetButtonTitle="Effacer la recherche" />
        </div>
      </div>
      <div className="pcv3-completed-scroll" role="region" aria-label="Projets terminés" tabIndex={0}>
        <table className="nj-table nj-table--striped nj-table--hover pcv3-data-table pcv3-completed-table"
          data-testid="completed-projects-table" aria-label="Projets terminés">
          <colgroup><col /><col /><col /><col /><col /><col /></colgroup>
          <thead>
            <tr>
              {HEADERS.map(({ key, label }) => (
                <th scope="col" key={key} aria-sort={sort.key === key ? (sort.descending ? 'descending' : 'ascending') : 'none'}>
                  <button type="button" className="pcv3-sort-header" onClick={() => {
                    setSort({ key, descending: sort.key === key ? !sort.descending : false });
                    setPage(1);
                  }}>
                    <NJText as="span" scale="xs">{label}{sort.key === key ? (sort.descending ? ' ↓' : ' ↑') : ''}</NJText>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((project) => (
              <tr key={project.id}>
                <th scope="row"><NJLink size="sm" variant="contextual" href={`#project-${project.id}`} onClick={(event) => {
                  event.preventDefault(); onOpen(project.id);
                }}>{project.name}</NJLink></th>
                <td><NJText as="span" scale="sm">{project.communes}</NJText></td>
                <td><NJText as="span" scale="sm">{project.updatedAt}</NJText></td>
                <td><NJText as="span" scale="sm">{contractMonth(project.start)}</NJText></td>
                <td><NJText as="span" scale="sm">{contractMonth(project.end)}</NJText></td>
                <td><div className="pcv3-table-avatars"><ProjectAccessAvatars project={project} /></div></td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6}><NJText scale="sm">{projects.length ? 'Aucun projet ne correspond à la recherche.' : 'Aucun projet terminé.'}</NJText></td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="pcv3-table-pagination">
        <div className="pcv3-page-size">
          <NJSelect.Root id="pcv3-page-size" label="Lignes par page" labelKind="static"
            listNavigationLabel="Utilisez les flèches puis Entrée pour choisir."
            buttonDefaultValueLabel="Choisir le nombre de lignes" initialValue="50"
            onChange={(value) => {
              if (value) { setPageSize(Number(value)); setPage(1); }
            }}>
            {[20, 50, 100, 500, 1000].map((size) => (
              <NJSelect.Item key={size} value={String(size)} label={String(size)}>{String(size)}</NJSelect.Item>
            ))}
          </NJSelect.Root>
        </div>
        <NJText scale="sm" role="status">{rows.length ? start + 1 : 0}–{Math.min(start + pageSize, rows.length)} sur {rows.length} projet{rows.length !== 1 ? 's' : ''}</NJText>
        <NJPagination key={pageCount} count={pageCount} currentItem={currentPage}
          ariaLabelPrevious="Page précédente des projets terminés"
          ariaLabelNext="Page suivante des projets terminés"
          onChange={(item) => setPage(item)} />
      </div>
    </section>
  );
}
